import { getElementAbsolutePosition } from '@/assets/js/utils';
import PermissionLevel from '@/assets/js/enum/PermissionLevel';
//
// state
const state = {
  dateFormat: { year: 'numeric', month: 'short', day: 'numeric' },

  appLoading: false,
  appUnauthorizedResource: false,
  appResourceNotFound: false,

  defaultCharacterLimit: 280,

  /**
     * Toast Alert message -
     * Used to display a single toast alert to the user.
     * Required format:
        {
            title: string,
            message: string,
        }
     */
  toastAlert: {},

  tooltips: {
    icons: {
      articleDetails: 'Debate Insights',
      documentWideInstructions: 'Open the Document-wide Instructions popup menu',
      teamSettings: 'Manage Team Lineup',
    },
    permissionLevel: {
      teamLead: 'Organizes the team and finalizes submissions',
      teamMember: 'Recommends counterpoints supporting the Team Lead',
    },
    discourseRole: {
      title: "The Team Lead's role relative to the document being annotated",
      stakeholder: 'A person with a demonstrable substantial interest in the document',
      author: 'The author of the document',
    },
    roundNumber: {
      title: 'Your latest round on the debate',
    },
    status: {
      title: 'The most recent step in the annotation process',
      teamInstructions:
        'The Team Lead assigns document-wide instructions, In-Line instructions for selected texts, and mandatory annotation assignments for Team Members',
      assembleTeam: 'The Team Lead selects Team Members',
      composition: 'The Team Members compose their assigned annotations',
      selectionEditing:
        'The Team Lead edits the submitted Team Member annotations and selects final annotations for publishing',
      published:
        'The Team Lead publishes selected annotations. Further editing is prohibited from this point',
    },
    annotation: {
      leadInstructions: 'Write instructions for you team',
      memberInstructions: 'Write your annotations according to these instructions',
      showAnnotation: 'Show annotation',
      hideAnnotation: 'Hide annotation',
      teamReplies: qty => `Your team has included ${qty} annotations`,
    },
  },

  mainPanelView: 'article', // article | outline
  showingCounterpointSidePanel: false,
  scrollSource: 'window', // window | sidePanel,
  metaTitle: '',
};

//
// getters
const getters = {
  mainMenuItems() {
    return [
      {
        label: 'MyAccount',
        name: 'AccountDetails',
        tooltip: 'View and manage your account settings',
      },
      {
        label: 'MyDiscussions',
        name: 'Discussions',
        tooltip: 'View active debates',
      },
      {
        label: `MyInbox`,
        name: 'Inbox',
        tooltip: 'View invites and notifications',
      },
      {
        label: 'MyPeople',
        name: 'Contacts',
        tooltip: 'Manage your contacts',
      },
      ///////////
      // Disabled for 0.5.8 until Organizations are revisited.
      //
      // {
      //   label: "MyOrganizations",
      //   name: "Organizations",
      //   tooltip: "View your organizations and collaborate with invited team members",
      // },
      {
        label: 'Users',
        tooltip: '',
      },
    ];
  },

  isMobileScreenSize() {
    let w = document.documentElement.clientWidth || window.innerWidth;
    if (w <= 480) {
      // Probably mobile
      return true;
    } else {
      // Probably desktop
      return false;
    }
  },
  tableToggleData: () => (val, tableName) => {
    let rawDatas = localStorage.getItem('my-data');
    let rawData = rawDatas ? JSON.parse(rawDatas) : {};
    rawData[tableName] = val;
    localStorage.setItem('my-data', JSON.stringify(rawData));
  },
  getAdjustedTime: () => (date, threshold) => {
    return new Date(
      date.setHours(
        // set same time adjustment as backend to account for clock differences
        date.getHours() + parseInt(threshold?.toString().split('.')[0] || 0), // hours
        date.getMinutes() + parseInt(threshold?.toString().split('.')[1] || 0), // minutes
        0, // seconds
        0 // microseconds
      )
    );
  },

  sortAndFilter:
    state =>
    ({ data, sortColumn, sortDirection, filters }) => {
      if (sortColumn) {
        let firstValue = '',
          lastValue = '';
        if (sortDirection === 'asc') {
          data.sort((a, b) => {
            if (sortColumn === 'dateInvited') {
              firstValue = a.updatedAt;
              lastValue = b.updatedAt;
            } else {
              firstValue = sortColumn.includes('.')
                ? sortColumn.split('.').reduce((prev, curr) => prev?.[curr], a)
                : a[sortColumn];
              lastValue = sortColumn.includes('.')
                ? sortColumn.split('.').reduce((prev, curr) => prev?.[curr], b)
                : b[sortColumn];
            }
            const d = new Date(firstValue);
            if (
              /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(firstValue) &&
              d instanceof Date &&
              !isNaN(d) &&
              d.toISOString() === firstValue
            ) {
              if (new Date(firstValue).getTime() < new Date(lastValue).getTime()) {
                return -1;
              }
              if (new Date(firstValue).getTime() > new Date(lastValue).getTime()) {
                return 1;
              }
              return 0;
            } else if (typeof firstValue === 'string') {
              if (firstValue?.toLowerCase() < lastValue?.toLowerCase()) {
                return -1;
              }
              if (firstValue?.toLowerCase() > lastValue?.toLowerCase()) {
                return 1;
              }
              return 0;
            } else {
              if (firstValue < lastValue) {
                return -1;
              }
              if (firstValue > lastValue) {
                return 1;
              }
              return 0;
            }
          });
        } else {
          data.sort((a, b) => {
            if (sortColumn === 'dateInvited') {
              firstValue = a.updatedAt;
              lastValue = b.updatedAt;
            } else {
              firstValue = sortColumn.includes('.')
                ? sortColumn.split('.').reduce((prev, curr) => prev?.[curr], a)
                : a[sortColumn];
              lastValue = sortColumn.includes('.')
                ? sortColumn.split('.').reduce((prev, curr) => prev?.[curr], b)
                : b[sortColumn];
            }
            const d = new Date(firstValue);
            if (
              /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(firstValue) &&
              d instanceof Date &&
              !isNaN(d) &&
              d.toISOString() === firstValue
            ) {
              if (new Date(lastValue).getTime() < new Date(firstValue).getTime()) {
                return -1;
              }
              if (new Date(lastValue).getTime() > new Date(firstValue).getTime()) {
                return 1;
              }
              return 0;
            } else if (typeof firstValue === 'string' && firstValue && lastValue) {
              if (lastValue.toLowerCase() < firstValue.toLowerCase()) {
                return -1;
              }
              if (lastValue.toLowerCase() > firstValue.toLowerCase()) {
                return 1;
              }
              return 0;
            } else {
              if (lastValue < firstValue) {
                return -1;
              }
              if (lastValue > firstValue) {
                return 1;
              }
              return 0;
            }
          });
        }
      }
      if (!filters?.archived?.value) {
        data = data.filter(item => !item.archived);
      }

      Object.keys(filters).map(key => {
        if (key !== 'searchScope' && key !== 'archived' && filters[key].value?.length) {
          if (key === 'invitee') {
            data = data.filter(item => {
              let found = filters[key].value.filter(value => {
                if (value === 'Me') {
                  if (state.userData.id !== item.invitation?.invitedBy?._id) return value;
                } else if (
                  value ===
                  (item.invitation?.invitee?.firstname || '') +
                    ' ' +
                    (item.invitation?.invitee?.lastname || '')
                )
                  return value;
              }).length;
              if (found) return item;
            });
          } else {
            let values = [];
            data = data.filter(item => {
              if (key === 'global') {
                values = filters.searchScope.map(key => {
                  if (key === 'full_name') {
                    return item['firstname'].toLowerCase() + ' ' + item['lastname'].toLowerCase();
                  } else if (key === 'invitee') {
                    if (filters.global.value?.toLowerCase() === 'me') {
                      if (state.userData.id !== item.invitation?.invitedBy?._id) return 'me';
                    }
                    return (
                      item['invitation']['invitee']['firstname'].toLowerCase() +
                      ' ' +
                      item['invitation']['invitee']['lastname'].toLowerCase()
                    );
                  } else if (key === 'invitation.invitedBy.full_name') {
                    if (filters.global.value?.toLowerCase() === 'me') {
                      if (state.userData.id === item.invitation?.invitedBy?._id) return 'me';
                    }
                  }
                  return key.includes('.')
                    ? key
                        .split('.')
                        .reduce(
                          (prev, curr) =>
                            curr === 'full_name'
                              ? prev?.['firstname'] + ' ' + prev?.['lastname']
                              : prev?.[curr],
                          item
                        )
                        ?.toLowerCase()
                    : item[key]?.toLowerCase();
                });
              } else {
                values =
                  key === 'full_name'
                    ? [item['firstname']?.toLowerCase() + ' ' + item['lastname']?.toLowerCase()]
                    : key.includes('.')
                      ? [
                          key
                            .split('.')
                            .reduce((prev, curr) => prev?.[curr], item)
                            ?.toLowerCase(),
                        ]
                      : [
                          typeof item[key] === 'string'
                            ? item[key]?.toLowerCase()
                            : item[key]?.map(value => value?.toLowerCase()),
                        ];
              }
              if (typeof filters[key].value === 'string') {
                if (
                  values.filter(value => value?.includes(filters[key].value?.toLowerCase())).length
                )
                  return item;
              } else {
                let found = values.filter(value => {
                  if (typeof value === 'string') {
                    if (
                      filters[key].value.findIndex(
                        item =>
                          value?.toLowerCase() ===
                          (item.name ? item.value?.toLowerCase() : item?.toLowerCase())
                      ) !== -1
                    )
                      return value;
                  } else {
                    if (
                      filters[key].value.filter(item =>
                        value?.includes(item.name ? item.value?.toLowerCase() : item?.toLowerCase())
                      ).length
                    )
                      return value;
                  }
                }).length;
                if (found) return item;
              }
            });
          }
        }
      });
      return data;
    },

  formatDate: state => value => new Date(value).toLocaleDateString('en-us', state.dateFormat),

  getGeometryObj: () => element => {
    let position = getElementAbsolutePosition(element);
    return {
      top: position.top,
      left: position.left,
      height: element.offsetHeight,
      width: element.offsetWidth,
    };
  },

  ukTooltip: () => (text, position, delay) => {
    const defaultPosition = 'top';
    const defaultDelay = 500;
    return `title: ${text}; pos: ${position || defaultPosition}; delay: ${delay || defaultDelay}`;
  },

  useExcerpts: (state, _getters, rootState, _rootGetters) => {
    const roundsLength = state.counterpointData?.rounds?.length || 0;
    const permissionLevel = rootState.debateStatus?.permissionLevel;

    const isCurrentUserCohort = permissionLevel === PermissionLevel.COHORT;

    const hasTeamData = rootState.currentTeam?.members !== undefined;

    // Check if any team member has cohort permission
    // Only check if team data is available
    const hasTeamMemberWithCohort = hasTeamData
      ? rootState.currentTeam?.members?.some(
          member =>
            (member.status === 'Accepted' || member.status === 'Invited') &&
            member.teamRole === PermissionLevel.COHORT
        )
      : false;

    const isCohortTeam = isCurrentUserCohort || hasTeamMemberWithCohort;
    let result;

    // If team data hasn't loaded yet and user is not individually cohort,
    // default to regular (non-cohort) behavior to avoid blank screens
    if (!hasTeamData && !isCurrentUserCohort) {
      // Team data not loaded yet, use regular behavior (rounds > 3)
      result = roundsLength > 3;
    } else if (isCohortTeam) {
      // For cohort users or teams with cohort members, enable excerpts for rounds up to and including 4
      result = roundsLength > 4;
    } else {
      // For other users, keep the original behavior (rounds > 3)
      result = roundsLength > 3;
    }

    return result;
  },

  metatags: () => (title, description, imageURL) => {
    const metaProperties = {
      'og:title': title,
      description,
      'og:description': description,
      'og:image': imageURL,
      'og:image:type': 'image/png',
      'og:image:width': '1200',
      'og:image:height': '628',
      'twitter:image': imageURL,
      'twitter:card': 'summary_large_image',
      'twitter:site': '@objectively',
      test: 'test',
    };
    return Object.entries(metaProperties).map(([property, content]) => {
      return {
        vmid: property,
        name: property,
        property,
        content,
      };
    });
  },
  metaInfo: (state, getters) => {
    const documentTitle =
      state.metaTitle === 'Debate'
        ? 'Debate on ' + (state.documentData?.title ?? 'Objectively')
        : state.metaTitle;
    const title = documentTitle ? documentTitle : 'Debate';
    const description = documentTitle ? `Debate on ${documentTitle}` : 'Debate';
    const imageName = documentTitle ? documentTitle.replace(/[^A-Za-z0-9]/g, '-') : 'default';
    const imagePath =
      'https://noetic1storage.blob.core.windows.net/objectively-social-images' +
      '/' +
      `${state?.publicLinkData?._id ?? 'NO_PUBLIC_LINK_ID'}` +
      '/' +
      `${state?.publicTargetAnnotationId ?? 'NO_PUBLIC_TARGET_ANNOTATION_ID'}` +
      '/' +
      `${state?.publicTargetNetwork ?? 'NO_PUBLIC_TARGET_NETWORK'}` +
      '/' +
      `${imageName}.png`;
    return {
      title,
      meta: getters.metatags(title, description, imagePath),
    };
  },
  imagePath: state => {
    const documentTitle = state.documentData?.title ?? 'Objectively';
    const imageName = documentTitle ? documentTitle.replace(/[^A-Za-z0-9]/g, '-') : 'default';
    const imagePath =
      'https://noetic1storage.blob.core.windows.net/objectively-social-images' +
      `/${state?.publicLinkData?._id ?? 'NO_PUBLIC_LINK_ID'}` +
      (state?.publicTargetAnnotationId ? `/${state.publicTargetAnnotationId}` : '') +
      `/${state?.publicTargetNetwork ?? 'NO_PUBLIC_TARGET_NETWORK'}` +
      `/${imageName}.png`;
    console.debug(' ... imagePath:', imagePath);

    return imagePath;
  },
};

//
// mutations
const mutations = {
  setAppLoading(state, loading) {
    state.appLoading = loading;
  },
  setMetaTitle(state, title) {
    state.metaTitle = title;
  },

  // @TODO: verify implementation of authorized content
  setAppUnauthorizedResource(state, unauthorized) {
    state.appUnauthorizedResource = unauthorized;
  },
  setAppResourceNotFound(state, notFound) {
    state.appResourceNotFound = notFound;
  },

  setMainPanelView(state, source) {
    state.mainPanelView = source;
  },
  toggleCounterpointSidePanel: state =>
    (state.showingCounterpointSidePanel = !state.showingCounterpointSidePanel),
  showCounterpointSidePanel: (state, value) => {
    console.log('showCounterpointSidePanel :: value', value);
    state.showingCounterpointSidePanel = value;
  },
  setScrollSource(state, source) {
    state.scrollSource = source;
  },
};

//
// actions
const actions = {
  /**
   * Legacy function for keeping track of key element's positions
   * To be used along with legacy scrolling and legacy arrow positioning
   * Kept for reference (NCH 1/15/23)
   */
  // setGeometry({ getters }, { dataObject, selectionElement }) {
  //   if (!(dataObject && selectionElement)) return;

  //   if (!dataObject.geometry)
  //     Vue.set(dataObject, "geometry", getters.getGeometryObj(selectionElement));
  //   else
  //     dataObject.geometry = {
  //       ...dataObject.geometry,
  //       ...getters.getGeometryObj(selectionElement),
  //     };
  // },

  /**
   * Show Toast Alert
   * Display a single toast alert message to the user
   * @title string _required_ - main error issue
   * @message string - explanatory reason
   */
  showToastAlert({ state }, { title, message }) {
    state.toastAlert = {
      title,
      message,
    };
  },

  /**
   * Hide Toast Alert
   * Hides from UI a single toast alert message
   */
  hideToastAlert({ state }) {
    state.toastAlert = {};
  },

  openBrowserModal(context, { window, url, title }) {
    const popupParams = `status=no,location=no,toolbar=no,menubar=no,width=600,height=600,left=100,top=100`;
    return window.open(url, title, popupParams);
  },

  openTwitterBrowserModal(
    { state, dispatch },
    { window, linkId, docId, docTitle, annotation, url, message, visitorId }
  ) {
    let twitterLoginURL = `${window.location.origin}/#/social/twitter/login?linkId=${linkId}`;

    if (!message) {
      message = `Check this debate on "${docTitle}"`;
    }

    window.TwitterPopupGetParams = () => {
      const userId = state.userData?.id;
      return {
        linkId,
        docId,
        docTitle,
        annotation,
        url,
        message,
        visitorId,
        userId,
      };
    };

    dispatch('openBrowserModal', {
      window,
      url: twitterLoginURL,
      title: 'Twitter Post',
    });
  },
};

//
// exports
export default {
  // namespaced: false,
  state,
  getters,
  actions,
  mutations,
};
